import React, { useRef, useState, useEffect } from 'react';
import { checkValidateData } from '../utils/validate'; 
import { LOGIN_URL } from '../utils/constants';
import { useNavigate } from 'react-router-dom';
import { addUser } from '../utils/userSlice';
import { useDispatch } from 'react-redux';  
import logo from '../assets/images/logo.webp';

const BASE_URL = process.env.REACT_APP_API_URL || '/';

const Login = () => {
  const dispatch = useDispatch();  
  const Navigate = useNavigate();     
  const email = useRef(null);
  const password = useRef(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState({
    signin_title: 'Naka Job Portal Admin Login',
    signin_subtitle: 'Sign in to your admin account',
    signin_background_color: '#F1F5F9',
    footer_text: 'By signing in, you agree to our Terms of Use and Privacy Policy.',
    logo_url: '',
    app_name: 'Naka Job Portal',
  });

  useEffect(() => {
    fetchPublicSettings();
  }, []);

  const fetchPublicSettings = async () => {
    try {
      const response = await fetch(`${BASE_URL}api/public-settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings({
          signin_title: data.signin?.signin_title || 'Naka Job Portal Admin Login',
          signin_subtitle: data.signin?.signin_subtitle || 'Sign in to your admin account',
          signin_background_color: data.signin?.signin_background_color || '#F1F5F9',
          footer_text: data.signin?.footer_text || 'By signing in, you agree to our Terms of Use and Privacy Policy.',
          logo_url: data.branding?.logo_url || '',
          app_name: data.general?.app_name || 'Naka Job Portal',
        });
      }
    } catch (error) {
      console.log('Using default settings');
    }
  };

  const handleSubmit = async () => {
    const message = checkValidateData(
      email.current.value,
      password.current.value
    );

    setErrorMessage(message);
    if (message) return;  

    setIsLoading(true);
    const formData = {
      email: email.current.value,
      password: password.current.value,
    };

    try {
      const response = await fetch(LOGIN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      const data = await response.json();   

      if (data.error) {
        setErrorMessage(data.error);
        setIsLoading(false);
        return; 
      }
      
      if (data.sucess === true) {
        dispatch(addUser(data?.data));
        Navigate("/dashboard");
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('An error occurred. Please try again.');
    }
    setIsLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex"
      style={{ backgroundColor: settings.signin_background_color }}
    >
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-400/10 rounded-full blur-2xl"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-lg">
          <div className="mb-8">
            <div className="w-20 h-20 bg-white/20 rounded-2xl mx-auto flex items-center justify-center backdrop-blur-sm mb-6">
              <i className="bi bi-briefcase-fill text-4xl"></i>
            </div>
            <h1 className="text-4xl font-bold mb-4">{settings.app_name}</h1>
            <p className="text-xl text-blue-100">{settings.signin_title}</p>
          </div>
          
          <div className="space-y-6 text-left bg-white/10 backdrop-blur-sm rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="bi bi-people text-lg"></i>
              </div>
              <div>
                <h3 className="font-semibold">Manage Candidates</h3>
                <p className="text-sm text-blue-100">Track and manage job seekers efficiently</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="bi bi-building text-lg"></i>
              </div>
              <div>
                <h3 className="font-semibold">Employer Portal</h3>
                <p className="text-sm text-blue-100">Connect with top companies and employers</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="bi bi-graph-up-arrow text-lg"></i>
              </div>
              <div>
                <h3 className="font-semibold">Analytics Dashboard</h3>
                <p className="text-sm text-blue-100">Get insights into job market trends</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-6">
                {settings.logo_url ? (
                  <img src={settings.logo_url} alt="Logo" className="h-16 object-contain" />
                ) : (
                  <img src={logo} alt="Logo" className="h-16 object-contain" />
                )}
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Sign In</h2>
              <p className="text-gray-500 mt-2">{settings.signin_subtitle}</p>
            </div>

            {errorMessage && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                <i className="bi bi-exclamation-circle-fill text-red-500"></i>
                <span className="text-red-700 text-sm">{errorMessage}</span>
              </div>
            )}

            <form onSubmit={(e) => e.preventDefault()} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="bi bi-envelope text-gray-400"></i>
                  </div>
                  <input
                    ref={email}
                    type="email"
                    id="email"
                    autoComplete="email"
                    placeholder="admin@example.com"
                    className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900 placeholder-gray-400"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <i className="bi bi-lock text-gray-400"></i>
                  </div>
                  <input
                    ref={password}
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900 placeholder-gray-400"
                  />
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  <>
                    <i className="bi bi-box-arrow-in-right"></i>
                    Sign In
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-100">
              <p className="text-center text-sm text-gray-500">
                {settings.footer_text || 'By signing in, you agree to our Terms of Use and Privacy Policy.'}
              </p>
            </div>
          </div>

          <p className="text-center text-sm text-gray-400 mt-6">
            &copy; {new Date().getFullYear()} {settings.app_name}. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
